// Change orders after lock: what changed between the revision the shop is building to and
// the job as it stands now, and what that does to work in progress.
//
//   - Deltas: price and hours against the prior revision.
//   - Cards: a card not yet pulled is regenerated; a card in flight is never rewritten, it is
//     flagged "Changed in Rev N" for the worker to acknowledge; a completed card is never
//     altered (rework is a new card).
//   - Purchasing: more of a material is a line to buy; less of one already on an open order is
//     flagged and never cancelled.
// A warranty fix is not a change order and never becomes one.

import { Rat } from './rational.ts';
import type { GeneratedCard } from './cards.ts';
import type { ResolvedGroup, ResolvedItem, Totals } from './types.ts';

/** A resolution, live or as stored with a locked revision. */
export interface ResolutionLike {
  items: Pick<ResolvedItem, 'id' | 'number' | 'name' | 'inputs' | 'price_cents' | 'labor_minutes'>[];
  groups: Pick<ResolvedGroup, 'bom'>[];
  totals: Pick<Totals, 'price_cents' | 'labor_minutes'>;
}

export interface ItemChange {
  id: string;
  number: string;
  name: string;
  /**
   * added, removed, or changed (a field on it is different). repriced: nothing on the item
   * itself changed, but its price or hours moved because it shares work with an item that
   * did, or because a standard moved. A repriced item is counted in the money, not in what
   * the shop is told changed.
   */
  kind: 'added' | 'removed' | 'changed' | 'repriced';
  /** What is different, in words: "Width 24 → 30". Empty for an added, removed or repriced item. */
  what: string[];
}

export interface MaterialDelta {
  material_id: string;
  description: string;
  unit: string | null;
  before: string;
  after: string;
  /** after − before, decimal text; negative when the job now needs less. */
  delta: string;
}

export interface RevisionDelta {
  price_delta_cents: number;
  /** Minutes, 3 places, as the engine keeps them. */
  minutes_delta: string;
  /** Hours, 2 places. */
  hours_delta: string;
  items: ItemChange[];
  materials: MaterialDelta[];
  /** The ids of the items added, removed or changed (not the merely repriced): what a card is checked against. */
  changed_item_ids: string[];
}

const text = (v: unknown) => (v === null || v === undefined || v === '' ? 'blank' : String(v));

function materialTotals(groups: ResolutionLike['groups']): Map<string, { description: string; unit: string | null; qty: Rat }> {
  const m = new Map<string, { description: string; unit: string | null; qty: Rat }>();
  for (const g of groups) {
    for (const b of g.bom) {
      if (!b.material_id) continue;
      const have = m.get(b.material_id) ?? { description: b.description, unit: b.unit, qty: Rat.ZERO };
      have.qty = have.qty.add(Rat.tryParse(b.qty) ?? Rat.ZERO);
      m.set(b.material_id, have);
    }
  }
  return m;
}

/** What changed from the prior revision to the job as it stands. */
export function revisionDelta(prior: ResolutionLike, next: ResolutionLike): RevisionDelta {
  const minutes = Rat.parse(next.totals.labor_minutes).sub(Rat.parse(prior.totals.labor_minutes));
  const before = new Map(prior.items.map((i) => [i.id, i]));
  const after = new Map(next.items.map((i) => [i.id, i]));
  const items: ItemChange[] = [];

  for (const n of next.items) {
    const p = before.get(n.id);
    if (!p) {
      items.push({ id: n.id, number: n.number, name: n.name, kind: 'added', what: [] });
      continue;
    }
    const what: string[] = [];
    const was = new Map(p.inputs.map((i) => [i.key, i]));
    for (const i of n.inputs) {
      const o = was.get(i.key);
      if (text(o?.value) !== text(i.value)) what.push(`${i.label} ${text(o?.value)} → ${text(i.value)}`);
    }
    for (const o of p.inputs) if (!n.inputs.some((i) => i.key === o.key)) what.push(`${o.label} ${text(o.value)} → no longer asked`);
    if (p.number !== n.number) what.push(`Number ${p.number} → ${n.number}`);
    if (what.length > 0) items.push({ id: n.id, number: n.number, name: n.name, kind: 'changed', what });
    else if (p.price_cents !== n.price_cents || Rat.parse(p.labor_minutes).cmp(Rat.parse(n.labor_minutes)) !== 0) {
      items.push({ id: n.id, number: n.number, name: n.name, kind: 'repriced', what: [] });
    }
  }
  for (const p of prior.items) if (!after.has(p.id)) items.push({ id: p.id, number: p.number, name: p.name, kind: 'removed', what: [] });

  const was = materialTotals(prior.groups);
  const now = materialTotals(next.groups);
  const materials: MaterialDelta[] = [];
  for (const id of new Set([...was.keys(), ...now.keys()])) {
    const a = was.get(id);
    const b = now.get(id);
    const d = (b?.qty ?? Rat.ZERO).sub(a?.qty ?? Rat.ZERO);
    if (d.isZero()) continue;
    materials.push({
      material_id: id,
      description: (b ?? a)!.description,
      unit: (b ?? a)!.unit,
      before: (a?.qty ?? Rat.ZERO).toDecimal(4),
      after: (b?.qty ?? Rat.ZERO).toDecimal(4),
      delta: d.toDecimal(4),
    });
  }
  materials.sort((x, y) => x.description.localeCompare(y.description));

  return {
    price_delta_cents: next.totals.price_cents - prior.totals.price_cents,
    minutes_delta: minutes.toDecimal(3),
    hours_delta: minutes.div(Rat.of(60n)).toFixed(2),
    items,
    materials,
    changed_item_ids: items.filter((i) => i.kind !== 'repriced').map((i) => i.id),
  };
}

// ---------- what an approved change order does to the cards ----------

export interface ExistingCard {
  id: string;
  number: number;
  name: string;
  /** unpulled: never started. in_flight: pulled, active or held after a pull. done: completed. */
  status: 'unpulled' | 'in_flight' | 'done';
  item_ids: string[];
}

export interface CardPlan {
  /** Unpulled cards to take off the board: they are regenerated. */
  remove: string[];
  /** Cards to put on the board from the new revision. */
  create: GeneratedCard[];
  /** Cards that stay as they are and are told the job changed under them. */
  flag: { card_id: string; changes: string[] }[];
  /** Completed cards whose items changed: never altered; listed so someone decides on rework. */
  rework: { card_id: string; number: number; name: string; changes: string[] }[];
}

/** A card split by hand carries its parent's name with a suffix: "Build · Boxes (part 2)". */
const sameWork = (existing: string, generated: string) => existing === generated || existing.startsWith(`${generated} (`);

/**
 * Regenerate what has not started; leave alone what has. A card generated from the new
 * revision is put on the board unless the same work is already in flight or done.
 */
export function planCardsAfterChange(existing: ExistingCard[], generated: GeneratedCard[], delta: Pick<RevisionDelta, 'items'>): CardPlan {
  const changeOf = new Map(delta.items.filter((i) => i.kind !== 'repriced').map((i) => [i.id, i]));
  const changesFor = (itemIds: string[]): string[] =>
    [...new Set(itemIds)]
      .map((id) => changeOf.get(id))
      .filter((c): c is ItemChange => !!c)
      .map((c) => (c.kind === 'added' ? `${c.number} added` : c.kind === 'removed' ? `${c.number} removed` : `${c.number}: ${c.what.join('; ')}`));

  const kept = existing.filter((c) => c.status !== 'unpulled');
  const plan: CardPlan = { remove: existing.filter((c) => c.status === 'unpulled').map((c) => c.id), create: [], flag: [], rework: [] };

  for (const g of generated) {
    if (kept.some((c) => sameWork(c.name, g.name))) continue; // already in flight or done: never rewritten
    plan.create.push(g);
  }
  for (const c of kept) {
    // the card's own items, and the items the same work carries in the new revision (a box added to its run)
    const changes = changesFor([...c.item_ids, ...generated.filter((g) => sameWork(c.name, g.name)).flatMap((g) => g.item_ids)]);
    if (changes.length === 0) continue;
    if (c.status === 'in_flight') plan.flag.push({ card_id: c.id, changes });
    else plan.rework.push({ card_id: c.id, number: c.number, name: c.name, changes });
  }
  return plan;
}

/** The changes a newly generated card should be flagged with: it replaces one the worker may have read. */
export function changesOnCard(card: Pick<GeneratedCard, 'item_ids'>, delta: Pick<RevisionDelta, 'items'>): string[] {
  const touched = new Set(card.item_ids);
  return delta.items
    .filter((c) => touched.has(c.id) && c.kind !== 'removed' && c.kind !== 'repriced')
    .map((c) => (c.kind === 'added' ? `${c.number} added` : `${c.number}: ${c.what.join('; ')}`));
}

// ---------- what it does to purchasing ----------

export interface PurchasingChange {
  material_id: string;
  description: string;
  unit: string | null;
  kind: 'add' | 'remove';
  /** How much more, or how much less, the job needs. Always positive. */
  qty: string;
  /** For a remove: how much of this material is on open orders for the job. Never cancelled by the system. */
  on_open_orders: string;
  /** True when a remove meets an open order: someone has to decide what to do with it. */
  flagged: boolean;
}

/** The material delta as lines for purchasing: more to buy, or less needed. */
export function purchasingAfterChange(materials: MaterialDelta[], onOpenOrders: Record<string, string | number> = {}): PurchasingChange[] {
  return materials.map((m) => {
    const d = Rat.parse(m.delta);
    const open = Rat.tryParse(onOpenOrders[m.material_id] ?? 0) ?? Rat.ZERO;
    const remove = d.sign() < 0;
    return {
      material_id: m.material_id,
      description: m.description,
      unit: m.unit,
      kind: remove ? 'remove' : 'add',
      qty: d.abs().toDecimal(4),
      on_open_orders: remove ? open.toDecimal(4) : '0',
      flagged: remove && open.sign() > 0,
    };
  });
}

/** The states a change order moves through. Their names are a setting; these keys are fixed. */
export const CHANGE_ORDER_STATES = ['draft', 'sent', 'approved', 'declined'] as const;
export type ChangeOrderState = (typeof CHANGE_ORDER_STATES)[number];

/** Where a change order may go from where it is. */
export function nextChangeOrderStates(state: ChangeOrderState): ChangeOrderState[] {
  if (state === 'draft') return ['sent', 'approved', 'declined'];
  if (state === 'sent') return ['approved', 'declined'];
  return [];
}

/** What the office still owes on an approved change order: the invoice line and the client notice. */
export function changeOrderDues(co: { state: string; price_delta_cents?: number | null; invoice_line_done_at?: string | null; client_notice_done_at?: string | null }): { invoice_line_due: boolean; client_notice_due: boolean } {
  const approved = co.state === 'approved';
  return {
    invoice_line_due: approved && !co.invoice_line_done_at && Number(co.price_delta_cents ?? 0) !== 0,
    client_notice_due: approved && !co.client_notice_done_at,
  };
}
